// ─── Impostazioni → Registro attività ────────────────────────────────────────
//
// Sola lettura, e non per pigrizia: le righe le scrive chi compie l'azione,
// nella stessa transazione dell'azione. Un registro che si può correggere da
// una schermata non è un registro.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { RigaRegistro } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { CampoData } from '../../components/CampoData.js';
import { Attesa, Campo, Guasto, Vuoto } from '../../components/pezzi.js';

export function Registro(): React.JSX.Element {
  const [azione, setAzione] = useState('');
  const [dal, setDal] = useState('');

  const pagina = useQuery({
    queryKey: ['registro', azione, dal],
    queryFn: () => {
      const q = new URLSearchParams({ limit: '50' });
      if (azione) q.set('action', azione);
      // L'input `date` dà un giorno; il filtro vuole un istante, e quello che
      // conta è la mezzanotte di qui — il fuso si tocca in un punto solo.
      if (dal) q.set('from', new Date(`${dal}T00:00:00`).toISOString());
      return chiama<{ items: RigaRegistro[]; nextCursor: string | null }>(
        'GET',
        `/settings/activity-log?${q.toString()}`,
      );
    },
  });

  return (
    <section>
      <header className="testata">
        <h2>Registro attività</h2>
      </header>

      <div className="filtri">
        <Campo
          etichetta="Azione (anche solo l’inizio)"
          placeholder="staff_user."
          value={azione}
          onChange={(e) => setAzione(e.target.value)}
        />
        <CampoData etichetta="Dal giorno" value={dal} onChange={setDal} />
      </div>

      {pagina.isLoading && <Attesa cosa="il registro" />}
      <Guasto errore={pagina.error} />

      {pagina.data &&
        (pagina.data.items.length === 0 ? (
          <Vuoto
            titolo="Nessuna riga"
            perché={
              azione || dal
                ? 'Con questi filtri non c’è niente. Non vuol dire che non sia successo niente.'
                : 'Non è ancora successo niente da registrare.'
            }
          />
        ) : (
          <div className="tabella-scorre">
            <table>
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Chi</th>
                  <th>Cosa</th>
                  <th>Su cosa</th>
                  <th>Prima → dopo</th>
                </tr>
              </thead>
              <tbody>
                {pagina.data.items.map((r) => (
                  <tr key={r.id}>
                    <td className="nowrap">{new Date(r.at).toLocaleString('it-IT')}</td>
                    {/* Nullo vuol dire «il sistema», non «non lo so»: sono due
                        cose diverse e si scrivono diverse. */}
                    <td>{r.actorEmail ?? <span className="tenue">sistema</span>}</td>
                    <td>
                      <code>{r.action}</code>
                    </td>
                    <td className="tenue piccolo">{r.entityType ?? '—'}</td>
                    <td className="piccolo">
                      {r.before || r.after ? (
                        <code>
                          {JSON.stringify(r.before)} → {JSON.stringify(r.after)}
                        </code>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      {pagina.data?.nextCursor && <p className="tenue piccolo">Ci sono altre righe più vecchie di queste.</p>}
    </section>
  );
}
